/**
 * Analytics tracking utilities
 *
 * Provides functions for encoding analytics tracking data for invisible pixel tracking
 */

/**
 * Encodes tracking data to base64 format for analytics pixel URL
 *
 * @param project - Project name (should not contain spaces)
 * @param source - Source identifier (e.g., "main")
 * @returns Base64 encoded string in format: p:{project},s:{source}
 *
 * @example
 * ```ts
 * encodeTrackingData("DevPulse", "main")
 * // Returns: "cDpEZXZQdWxzZSxzOm1haW4="
 * ```
 */
export function encodeTrackingData(project: string, source = "main"): string {
  // Remove spaces from project name
  const sanitizedProject = project.replace(/\s+/g, "")

  // Create the tracking string
  const trackingString = `p:${sanitizedProject},s:${source}`

  // Encode to base64
  return btoa(trackingString)
}

/**
 * Generates the analytics pixel URL
 *
 * @param project - Project name (should not contain spaces)
 * @param source - Source identifier (e.g., "main")
 * @returns Complete analytics pixel URL
 *
 * @example
 * ```ts
 * getAnalyticsPixelUrl("DevPulse", "main")
 * // Returns: "https://fly.urbanisierung.dev/analytics/image/cDpEZXZQdWxzZSxzOm1haW4="
 * ```
 */
export function getAnalyticsPixelUrl(project: string, source = "main"): string {
  const encoded = encodeTrackingData(project, source)
  return `https://fly.urbanisierung.dev/analytics/image/${encoded}`
}
